<script setup>
import {useBaseStore} from "~/stores/base";
import {Autoplay, Navigation, Pagination} from "swiper/modules";
import {Swiper, SwiperSlide} from "swiper/vue";
import {PATH_PRODUCT} from "~/utils/constants";
import _ from "lodash"
import {wishlistAdd} from "~/api/api.wishlist";
import {toLogin} from "~/utils/common";
import ModalWishlistCapsule from "~/components/wishlist/ModalWishlistCapsule.vue";
import {googleTrackLikeProduct} from "~/utils/google-track";

const BNTOMessage = useNuxtApp().$BNTOMessage
const debounce = _.debounce((params, func) => {
  func(params);
}, 300);
const router = useRouter()
const route = useRoute()

/** props **/
const props = defineProps({
  product: {
    default: {},
    required: true
  },
  current: {
    default: 0,
    required: true
  }
})

/** data **/
const likeEditingProduct = ref({})
const isShowModalWishlistCapsule = ref(false)

/** computed **/
const isMobile = computed(() => useBaseStore().getterIsMobile)
const currentColor = computed(() => props.product.newVariantOptions?.colorList[props.current].color)
const currentConfig = computed(() => props.product.optionConfig?.find(config => config.name === currentColor.value))
const isLogin = computed(() => useBaseStore().getterIsLogin)

/** methods **/
const onSwiper = (swiper) => {
};
const onSlideChange = () => {
};
const handleToProduct = (item) => {
  router.push(`${PATH_PRODUCT}/${item.shopProductId}/${item.handle}`)
}

const handleLike = async (item) => {
  if (!isLogin.value) return toLogin({query: {redirect_uri: route.fullPath}})
  const shopProductId = item.shopProductId
  likeEditingProduct.value = {
    ...item,
    productId: shopProductId,
    cover: item.cover
  }
  if (!item.isFollow) {
    await wishlistAdd({shopProductId})
    googleTrackLikeProduct(item)
    item.isFollow = true
    BNTOMessage.success({
      title: "saved to all",
      btnText: "add to capsule",
      onClick: () => {
        isShowModalWishlistCapsule.value = true
      }
    })
  } else {
    isShowModalWishlistCapsule.value = true
  }
}
const handleModalWishlistCapsuleClose = (liked) => {
  currentConfig.value.looks = currentConfig.value.looks.map((item) => {
    if (item.shopProductId === likeEditingProduct.value.productId) item.isFollow = liked
    return item
  })
  isShowModalWishlistCapsule.value = false
}
</script>

<template>
  <div class="module-product-suitable-dressing-mobile" v-if="isMobile && currentConfig?.looks.length">
    <div class="title">COMPLETE THE LOOK</div>
    <div class="carousel">
      <div class="item" v-for="item in currentConfig?.looks" @click="handleToProduct(item)">
        <div class="item-top" :style="{backgroundImage: `url(${item?.cover?.url})`}">
          <div class="like" @click.stop="handleLike(item)">
            <img v-if="item?.isFollow" src="@/assets/img/icon-liked.svg" alt="icon">
            <img v-else src="@/assets/img/icon-unlike.svg" alt="icon">
          </div>
        </div>
        <div class="item-bottom">
          <div class="brand">{{ item?.brand }}</div>
          <div class="name">{{ item?.title }}</div>
          <div class="price">Retail: ${{ item?.price }}</div>
        </div>
      </div>
    </div>
  </div>
  <div class="module-product-suitable-dressing-desktop" v-if="!isMobile && currentConfig?.looks.length">
    <div class="title">COMPLETE THE LOOK</div>
    <swiper
      v-if="currentConfig.name === currentColor"
      :loop="currentConfig.looks.length > 4"
      grab-cursor
      :autoplay="false"
      :navigation="{
          prevEl: '.navigation-left',
          nextEl: '.navigation-right'
        }"
      :modules="[Navigation, Pagination, Autoplay]"
      :slides-per-view="4"
      :slides-per-group="4"
      :space-between="8"
      @swiper="onSwiper"
      @slideChange="onSlideChange"
    >
      <swiper-slide v-for="item in currentConfig.looks" @click="handleToProduct(item)">
        <div class="slide-top" :style="{backgroundImage: `url(${item?.cover?.url})`}">
          <div class="like" @click.stop="handleLike(item)">
            <img v-if="item?.isFollow" src="@/assets/img/icon-liked.svg" alt="icon">
            <img v-else src="@/assets/img/icon-unlike.svg" alt="icon">
          </div>
        </div>
        <div class="slide-bottom">
          <div class="brand">{{ item?.brand }}</div>
          <div class="name">{{ item?.title }}</div>
          <div class="price">Retail: ${{ item?.price }}</div>
        </div>
      </swiper-slide>

      <!-- If we need navigation buttons -->
      <div class="navigation-left" v-if="currentConfig.looks.length > 4">
        <img src="@/assets/img/icon-left-small.svg" alt="left">
      </div>
      <div class="navigation-right" v-if="currentConfig.looks.length > 4">
        <img src="@/assets/img/icon-right-small.svg" alt="right">
      </div>
    </swiper>
  </div>
  <modal-wishlist-capsule
    :open="isShowModalWishlistCapsule"
    :product="likeEditingProduct"
    @close="handleModalWishlistCapsuleClose"
  />
</template>

<style scoped lang="scss">
@import "src/assets/config";

.module-product-suitable-dressing-mobile {
  margin-top: 24px;
  padding: 8px 16px 0 16px;

  .title {
    font-family: "TWK Lausanne";
    font-size: 14px;
    font-style: normal;
    font-weight: 650;
    line-height: 18px; /* 128.571% */
    letter-spacing: -0.056px;
    text-transform: uppercase;
  }

  .carousel {
    display: flex;
    overflow-x: scroll;
    overflow-y: hidden;
    margin-top: 10px;

    .item {
      flex-shrink: 0;
      margin-right: 10px;
      cursor: pointer;
      width: 27vw;
      height: 55.5vw;

      .item-top {
        width: 100%;
        height: 37.3vw;
        background: center center no-repeat;
        background-size: cover;
        position: relative;
        display: flex;
        justify-content: flex-end;
        padding: 2px;

        .like {
          cursor: pointer;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 32px;
          height: 32px;

          img {
            width: 24px;
            height: 24px;
          }
        }
      }

      .item-bottom {
        width: 100%;
        height: 74px;
        padding: 8px 8px 12px 8px;

        .brand {
          /* Text xs/Bold */
          font-family: "TWK Lausanne";
          font-size: 12px;
          font-style: normal;
          font-weight: 650;
          line-height: 18px; /* 150% */

          // 单行
          word-break: break-all;
          word-wrap: anywhere;
          display: -webkit-box;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          line-clamp: 1;
          -webkit-line-clamp: 1;
        }

        .name {
          /* Text xs/Regular */
          font-family: "TWK Lausanne";
          font-size: 12px;
          font-style: normal;
          font-weight: 300;
          line-height: 18px; /* 150% */

          // 单行
          word-break: break-all;
          word-wrap: anywhere;
          display: -webkit-box;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          line-clamp: 1;
          -webkit-line-clamp: 1;
        }

        .price {
          /* Text xs/Regular */
          font-family: "TWK Lausanne";
          font-size: 12px;
          font-style: normal;
          font-weight: 300;
          line-height: 18px; /* 150% */
        }
      }

      &:last-child {
        margin-right: 0px;
      }
    }
  }
}

.module-product-suitable-dressing-desktop {
  margin-top: 32px;

  .title {
    /* Eyebrow/large */
    font-family: "TWK Lausanne";
    font-weight: 650;
    font-size: 18px;
    font-style: normal;
    line-height: 18px; /* 100% */
    letter-spacing: -0.072px;
    text-transform: uppercase;
  }

  .swiper {
    margin-top: 16px;
    position: relative;

    .swiper-slide {
      width: 140px;
      height: 270px;
      display: flex;
      flex-direction: column;
      cursor: pointer;

      .slide-top {
        width: 100%;
        height: 196px;
        background: center center no-repeat;
        background-size: cover;
        display: flex;
        justify-content: flex-end;
        padding: 2px;

        .like {
          cursor: pointer;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 32px;
          height: 32px;

          img {
            width: 24px;
            height: 24px;
          }
        }
      }

      .slide-bottom {
        width: 100%;
        height: 74px;
        padding: 8px 8px 12px 8px;

        &:hover {
          text-decoration: underline;
        }

        .brand {
          /* Text xs/Bold */
          font-family: "TWK Lausanne";
          font-size: 12px;
          font-style: normal;
          font-weight: 650;
          line-height: 18px; /* 150% */

          // 单行
          word-break: break-all;
          word-wrap: anywhere;
          display: -webkit-box;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          line-clamp: 1;
          -webkit-line-clamp: 1;
        }

        .name {
          /* Text xs/Regular */
          font-family: "TWK Lausanne";
          font-size: 12px;
          font-style: normal;
          font-weight: 300;
          line-height: 18px; /* 150% */

          // 单行
          word-break: break-all;
          word-wrap: anywhere;
          display: -webkit-box;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          line-clamp: 1;
          -webkit-line-clamp: 1;
        }

        .price {
          /* Text xs/Regular */
          font-family: "TWK Lausanne";
          font-size: 12px;
          font-style: normal;
          font-weight: 300;
          line-height: 18px; /* 150% */
        }

      }
    }

    .navigation-left {
      position: absolute;
      display: flex;
      left: 0;
      top: 50%;
      z-index: 1;
      transform: translate(0, -50%);
      cursor: pointer;

      img {
        width: 31px;
        height: 31px;
      }
    }

    .navigation-right {
      position: absolute;
      display: flex;
      right: 0;
      top: 50%;
      z-index: 1;
      transform: translate(0, -50%);
      cursor: pointer;

      img {
        width: 31px;
        height: 31px;
      }
    }
  }
}
</style>
